import { useEffect, useMemo, useRef } from 'react';
import { Gauge, Phone, Video } from 'lucide-react';
import { CallControls } from './CallControls';
import type { CallQuality } from '../hooks/useCall';

type ActiveCallScreenProps = {
  isOpen: boolean;
  mode: 'voice' | 'video';
  peerName: string;
  status: string;
  error: string;
  muted: boolean;
  cameraOff: boolean;
  speakerOff: boolean;
  durationSeconds: number;
  quality: CallQuality;
  reconnecting: boolean;
  verificationCode: string;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  onToggleMute: () => void;
  onToggleCamera: () => void;
  onToggleSpeaker: () => void;
  onSwitchCamera: () => void;
  onEnd: () => void;
};

export function ActiveCallScreen({
  isOpen,
  mode,
  peerName,
  status,
  error,
  muted,
  cameraOff,
  speakerOff,
  durationSeconds,
  quality,
  reconnecting,
  verificationCode,
  localStream,
  remoteStream,
  onToggleMute,
  onToggleCamera,
  onToggleSpeaker,
  onSwitchCamera,
  onEnd,
}: ActiveCallScreenProps) {
  const shellRef = useRef<HTMLDivElement | null>(null);
  const localVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteVideoRef = useRef<HTMLVideoElement | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const speakerSupported = typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
  const formattedDuration = useMemo(() => {
    const minutes = Math.floor(durationSeconds / 60)
      .toString()
      .padStart(2, '0');
    const seconds = Math.floor(durationSeconds % 60)
      .toString()
      .padStart(2, '0');
    return `${minutes}:${seconds}`;
  }, [durationSeconds]);

  useEffect(() => {
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream]);

  useEffect(() => {
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = remoteStream;
      remoteVideoRef.current.muted = false;
      remoteVideoRef.current.volume = 1;
      if (remoteStream?.getAudioTracks().length) {
        console.info('CALL_REMOTE_AUDIO_ATTACHED');
      }
      remoteVideoRef.current
        .play()
        .then(() => console.info('CALL_REMOTE_AUDIO_PLAY_OK'))
        .catch(() => console.info('CALL_REMOTE_AUDIO_PLAY_ERROR tap to enable audio'));
      console.info('CALL_REMOTE_AUDIO_ELEMENT_STATE', getMediaElementState(remoteVideoRef.current));
    }
  }, [remoteStream, speakerOff]);

  useEffect(() => {
    const audio = remoteAudioRef.current;

    if (!audio) {
      return;
    }

    console.info('VOICE_REMOTE_AUDIO_ELEMENT_CREATED');
    audio.autoplay = true;
    (audio as HTMLAudioElement & { playsInline?: boolean }).playsInline = true;
    audio.setAttribute('playsinline', 'true');
    audio.muted = false;
    audio.volume = 1;
    audio.srcObject = mode === 'voice' ? remoteStream : null;

    if (mode === 'voice' && remoteStream?.getAudioTracks().length) {
      console.info('CALL_REMOTE_AUDIO_ATTACHED');
      console.info('VOICE_REMOTE_AUDIO_ATTACHED');
      const cleanupPlaybackRetry = playRemoteAudio(audio, remoteStream);
      console.info('CALL_REMOTE_AUDIO_ELEMENT_STATE', getMediaElementState(audio));
      console.info('VOICE_REMOTE_AUDIO_STATE', getMediaElementState(audio));
      return cleanupPlaybackRetry;
    }

    return undefined;
  }, [mode, remoteStream, speakerOff]);

  useEffect(() => {
    const media = mode === 'video' ? remoteVideoRef.current : remoteAudioRef.current;

    if (!media) {
      return;
    }

    const setSinkId = (media as HTMLMediaElement & { setSinkId?: (sinkId: string) => Promise<void> }).setSinkId;

    if (!setSinkId) {
      console.info('CALL_SPEAKER_SET_SINK_UNSUPPORTED');
      return;
    }

    setSinkId
      .call(media, 'default')
      .then(() => console.info('CALL_SPEAKER_SET_SINK_OK'))
      .catch(() => console.info('CALL_SPEAKER_SET_SINK_UNSUPPORTED'));
  }, [mode, speakerOff, remoteStream]);

  if (!isOpen) {
    return null;
  }

  const Icon = mode === 'video' ? Video : Phone;

  function handleFullscreen() {
    if (!document.fullscreenElement) {
      shellRef.current?.requestFullscreen?.();
      return;
    }

    document.exitFullscreen?.();
  }

  async function handlePictureInPicture() {
    if (localVideoRef.current && document.pictureInPictureEnabled) {
      await localVideoRef.current.requestPictureInPicture?.().catch(() => undefined);
    }
  }

  return (
    <div className="overlay call-overlay" role="dialog" aria-modal="true" aria-labelledby="active-call-title">
      <div className="active-call-shell" ref={shellRef}>
        <div className="active-call-header">
          <span className="permission-icon success">
            <Icon size={24} aria-hidden="true" />
          </span>
          <div>
            <p className="panel-kicker">{mode} call</p>
            <h2 id="active-call-title">{peerName || 'Connecting'}</h2>
            <p>{reconnecting ? 'Reconnecting...' : status}</p>
          </div>
        </div>

        <div className="call-metrics" aria-label="Call quality">
          <span className={`quality-pill ${quality.label}`}>
            <Gauge size={15} aria-hidden="true" />
            {quality.label}
          </span>
          <span>{formattedDuration}</span>
          <span>ICE {status}</span>
          <span>{quality.bitrateKbps ?? '--'} kbps</span>
          <span>{quality.latencyMs ?? '--'} ms</span>
          <span>loss {quality.packetsLost ?? '--'}</span>
          {verificationCode ? <span>verify {verificationCode}</span> : null}
        </div>

        {mode === 'video' ? (
          <div className="video-stage">
            <video className="remote-video" ref={remoteVideoRef} autoPlay playsInline />
            <button className="local-video-button" type="button" onClick={handlePictureInPicture} aria-label="Open local preview picture in picture">
              <video className="local-video" ref={localVideoRef} autoPlay playsInline muted />
            </button>
          </div>
        ) : (
          <div className="voice-stage">
            <span className="voice-pulse">
              <Phone size={42} aria-hidden="true" />
            </span>
            <p>Secure peer connection active when status becomes connected.</p>
          </div>
        )}
        <audio ref={remoteAudioRef} autoPlay />

        {error ? <p className="form-error">{error}</p> : null}

        <CallControls
          mode={mode}
          muted={muted}
          cameraOff={cameraOff}
          speakerOff={speakerOff}
          speakerSupported={speakerSupported}
          onToggleMute={onToggleMute}
          onToggleCamera={onToggleCamera}
          onToggleSpeaker={onToggleSpeaker}
          onSwitchCamera={onSwitchCamera}
          onFullscreen={handleFullscreen}
          onEnd={onEnd}
        />
      </div>
    </div>
  );
}

function getMediaElementState(element: HTMLMediaElement) {
  return {
    muted: element.muted,
    volume: element.volume,
    paused: element.paused,
    readyState: element.readyState,
  };
}

function playRemoteAudio(audio: HTMLAudioElement, remoteStream: MediaStream) {
  const retryEvents = ['click', 'pointerdown', 'touchend', 'keydown'] as const;
  const mediaEvents = ['loadedmetadata', 'canplay', 'canplaythrough'] as const;
  const remoteAudioTracks = remoteStream.getAudioTracks();

  function cleanupRetryListeners() {
    retryEvents.forEach((eventName) => document.removeEventListener(eventName, retryPlay));
    mediaEvents.forEach((eventName) => audio.removeEventListener(eventName, retryPlay));
    remoteAudioTracks.forEach((track) => track.removeEventListener('unmute', retryPlay));
  }

  function retryPlay() {
    audio
      .play()
      .then(() => {
        console.info('CALL_REMOTE_AUDIO_PLAY_OK');
        console.info('VOICE_REMOTE_AUDIO_PLAY_OK');
        cleanupRetryListeners();
      })
      .catch(() => {
        console.info('CALL_REMOTE_AUDIO_PLAY_ERROR tap to enable audio');
        console.info('VOICE_REMOTE_AUDIO_PLAY_ERROR tap to enable audio');
      });
  }

  mediaEvents.forEach((eventName) => audio.addEventListener(eventName, retryPlay));
  remoteAudioTracks.forEach((track) => track.addEventListener('unmute', retryPlay));

  audio
    .play()
    .then(() => {
      console.info('CALL_REMOTE_AUDIO_PLAY_OK');
      console.info('VOICE_REMOTE_AUDIO_PLAY_OK');
    })
    .catch(() => {
      console.info('CALL_REMOTE_AUDIO_PLAY_ERROR tap to enable audio');
      console.info('VOICE_REMOTE_AUDIO_PLAY_ERROR tap to enable audio');
      retryEvents.forEach((eventName) => document.addEventListener(eventName, retryPlay, { once: true }));
    });

  return cleanupRetryListeners;
}
